import { Alert, Snackbar, Slide } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { setAlertDetails } from "../../redux/slices/AlertDetailsSlice";
import { safeTranslate } from "../../helper/safeTranslate";

/** Map known backend keys to user-friendly messages */
const MESSAGE_MAP = {
  password_reset_code_sent: "A password reset code has been sent to your email.",
  invalid_token: "Invalid or expired code. Please request a new one.",
  invalid_credentials: "Invalid email or password. Please try again.",
  invalid_request: "Something went wrong. Please try again.",
  server_error: "Something went wrong on our end. Please try again later.",
  sms_send_failed:
    "We could not send the SMS verification code. Please check the phone number or try another verification method.",
  whatsapp_not_available:
    "WhatsApp verification is not available yet. Please use SMS instead.",
  token_cooldown: "Please wait before requesting a new code.",
};

/**
 * Convert a raw backend key to a readable message.
 * 1. Check the explicit map
 * 2. Try i18n translation
 * 3. Auto-humanize: replace underscores/dots with spaces, capitalize first letter
 */
const humanize = (raw, t) => {
  if (!raw) return "";
  if (MESSAGE_MAP[raw]) return MESSAGE_MAP[raw];

  // Try i18n (error namespace)
  const translated = safeTranslate(t, raw, null);
  if (translated && translated !== raw) return translated;

  // Auto-humanize: "user.not_found" → "User not found"
  const cleaned = raw.replace(/[_.]/g, " ").trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

const SlideTransition = (props) => <Slide {...props} direction="left" />;

const severityIcons = {
  success: <CheckCircleOutlineIcon fontSize="inherit" />,
  error: <ErrorOutlineIcon fontSize="inherit" />,
  warning: <WarningAmberIcon fontSize="inherit" />,
  info: <InfoOutlinedIcon fontSize="inherit" />,
};

export const AlertMessage = () => {
  const alert = useSelector((state) => state.alertDetails);
  const dispatch = useDispatch();
  const { t } = useTranslation("error");

  const alertMessage = humanize(alert.message, t);

  const handleClose = useCallback(
    (_event, reason) => {
      if (reason === "clickaway") return;
      dispatch(setAlertDetails({ type: "", message: "" }));
    },
    [dispatch]
  );

  useEffect(() => {
    if (alert.message) {
      const timer = setTimeout(() => {
        dispatch(setAlertDetails({ type: "", message: "" }));
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [alert.message, dispatch]);

  const severity = alert.type || "info";

  return (
    <Snackbar
      open={!!alertMessage}
      onClose={handleClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      TransitionComponent={SlideTransition}
      sx={{ mt: "60px" }}
    >
      <Alert
        onClose={handleClose}
        severity={severity}
        variant="filled"
        iconMapping={severityIcons}
        sx={{
          minWidth: 300,
          maxWidth: 420,
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          fontSize: "0.9rem",
          fontWeight: 500,
          alignItems: "center",
          "& .MuiAlert-icon": {
            fontSize: "1.4rem",
            opacity: 0.9,
          },
          "& .MuiAlert-action": {
            pt: 0,
          },
        }}
      >
        {alertMessage}
      </Alert>
    </Snackbar>
  );
};
